'use client';

/**
 * Heatmap Lab — a dev-only tuning panel for the analytics heat layers.
 *
 * Opt in with `?heatlab=1`. Renders nothing otherwise, so it is inert in production.
 * Every control mutates MapLibre paint properties (and re-derives bin weights) live on
 * the existing layers — no reload, no rebuild — so design/product can trial settings
 * directly on real data and read off a config to hard-code.
 */
import type { Map as MapLibreMap } from 'maplibre-gl';
import { useEffect, useMemo, useState } from 'react';
import { type HeatmapCategoryKey, isHeatmapCategoryKey } from '../data/heatmap-config';
import type { HeatmapCategoryStats, HeatmapWeightConfig, HeatmapWeightScale } from '../data/heatmap-weight';
import { applyHeatmapWeights, DEFAULT_HEATMAP_WEIGHT, weightDecileHistogram } from '../data/heatmap-weight';
import { areaLayerId, HEAT_LAYER_PREFIX, heatLayerId, markerLayerId, outlineLayerId, SOURCE_ID } from '../hooks/useHeatmapLayer/constants';
import { heatmapColorExpression } from '../hooks/useHeatmapLayer/paint';
import { useMapContext } from '../map-context';
type RampKey = 'production' | 'hue-locked' | 'stepped';
type DiskMode = 'off' | 'outline' | 'on';
type LabState = HeatmapWeightConfig & {
  readonly radiusAtZ3: number;
  readonly worldLockRadius: boolean;
  readonly intensity: number;
  readonly opacity: number;
  readonly ramp: RampKey;
  readonly disks: DiskMode;
  readonly markers: boolean;
  readonly hidden: readonly string[];
};
const INITIAL: LabState = {
  ...DEFAULT_HEATMAP_WEIGHT,
  radiusAtZ3: 14,
  worldLockRadius: true,
  intensity: 1,
  opacity: 0.9,
  ramp: 'production',
  disks: 'off',
  markers: true,
  hidden: []
};

/**
 * Named baselines bracketing the useful range of the design space. Each one answers a
 * different question, so they are starting points to tweak from, not a ranking.
 *
 * Every preset sets *every* field it cares about — including `hidden` — so clicking
 * through them is order-independent and reproducible.
 */
const PRESETS: Readonly<Record<string, Partial<LabState>>> = {
  // Regression reference: what the map looked like before the weight fix.
  'Old (broken)': {
    scale: 'linear-peak',
    hideBottomPercentile: 0,
    radiusAtZ3: 24,
    worldLockRadius: false,
    intensity: 2.8,
    opacity: 0.95,
    ramp: 'production',
    disks: 'on',
    markers: true,
    hidden: []
  },
  // Closest honest analogue of a classic heatmap: nothing discarded, gentle intensity.
  'Soft log': {
    scale: 'log-clamp',
    clampPercentile: 0.99,
    hideBottomPercentile: 0,
    radiusAtZ3: 16,
    worldLockRadius: true,
    intensity: 0.6,
    opacity: 0.85,
    ramp: 'production',
    disks: 'off',
    markers: true,
    hidden: ['deaths']
  },
  // Balanced: real gradient, tail trimmed enough to expose structure.
  Gradient: {
    scale: 'log-clamp',
    clampPercentile: 0.99,
    hideBottomPercentile: 0.35,
    radiusAtZ3: 14,
    worldLockRadius: true,
    intensity: 1,
    opacity: 0.9,
    ramp: 'production',
    disks: 'off',
    markers: true,
    hidden: ['deaths']
  },
  // Matches the shipped DEFAULT_HEATMAP_WEIGHT — "where are the bad spots?"
  'Peaks only': {
    scale: 'clamp',
    clampPercentile: 0.95,
    hideBottomPercentile: 0.6,
    radiusAtZ3: 12,
    worldLockRadius: true,
    intensity: 1,
    opacity: 0.95,
    ramp: 'production',
    disks: 'off',
    markers: true,
    hidden: ['deaths']
  },
  // Minimum ink: only the worst handful of cells survive. Good for a "top spots" cut.
  'Extreme peaks': {
    scale: 'clamp',
    clampPercentile: 0.98,
    hideBottomPercentile: 0.85,
    radiusAtZ3: 10,
    worldLockRadius: true,
    intensity: 1.1,
    opacity: 1,
    ramp: 'production',
    disks: 'off',
    markers: true,
    hidden: ['deaths']
  },
  // Answers "what routes do players fight along?" rather than "where is it worst?".
  // A wide kernel (>the ~8.8px bin pitch at z3) merges neighbouring bins into continuous
  // streets. That only works with a *compressive* scale and low intensity — `rank` puts
  // the median bin at w=0.5, so a kernel this wide saturates the city instantly.
  Corridors: {
    scale: 'log-clamp',
    clampPercentile: 0.99,
    hideBottomPercentile: 0.5,
    radiusAtZ3: 15,
    worldLockRadius: true,
    intensity: 0.7,
    opacity: 0.9,
    ramp: 'hue-locked',
    disks: 'off',
    markers: false,
    hidden: ['deaths']
  },
  // Hard colour steps read as contour tiers rather than a continuous fog.
  Tiered: {
    scale: 'rank',
    hideBottomPercentile: 0.5,
    radiusAtZ3: 11,
    worldLockRadius: true,
    intensity: 1,
    opacity: 0.9,
    ramp: 'stepped',
    disks: 'off',
    markers: true,
    hidden: ['deaths']
  },
  // Both categories stacked, on the hue-locked ramp so green/pink stay tellable apart
  // (the production cold→hot ramp can't: both categories converge on red at the top).
  //
  // Keep this preset mainly as evidence that stacking DOESN'T work: `kills` draws after
  // `deaths` and has ~2x the surviving bins, so green buries pink almost everywhere.
  // Comparing categories wants one-at-a-time, side-by-side, or a difference map — not
  // two heatmaps on top of each other.
  'Kills vs Deaths': {
    scale: 'clamp',
    clampPercentile: 0.95,
    hideBottomPercentile: 0.45,
    radiusAtZ3: 12,
    worldLockRadius: true,
    intensity: 1.7,
    opacity: 0.95,
    ramp: 'hue-locked',
    disks: 'off',
    markers: true,
    hidden: []
  },
  // Deaths alone: 43k events vs 10.8M kills, so it needs its own intensity to read at all.
  // This is the most product-relevant view — "where do players actually die?"
  'Deaths only': {
    scale: 'clamp',
    clampPercentile: 0.95,
    hideBottomPercentile: 0.45,
    radiusAtZ3: 13,
    worldLockRadius: true,
    intensity: 1.7,
    opacity: 0.95,
    ramp: 'hue-locked',
    disks: 'off',
    markers: true,
    hidden: ['kills']
  }
};

/** Hue held per category (green kills / pink deaths) so the two stay distinguishable. */
const RAMPS: Readonly<Record<Exclude<RampKey, 'production'>, Readonly<Partial<Record<HeatmapCategoryKey, unknown[]>>>>> = {
  'hue-locked': {
    kills: [0, 'rgba(0,0,0,0)', 0.18, 'rgba(20,90,15,0)', 0.3, 'rgba(30,130,20,0.55)', 0.55, 'rgba(70,200,35,0.8)', 0.78, 'rgba(140,255,60,0.92)', 1, 'rgba(225,255,170,1)'],
    deaths: [0, 'rgba(0,0,0,0)', 0.18, 'rgba(90,10,50,0)', 0.3, 'rgba(150,20,85,0.55)', 0.55, 'rgba(225,40,130,0.8)', 0.78, 'rgba(255,70,165,0.92)', 1, 'rgba(255,200,230,1)']
  },
  stepped: {
    kills: [0, 'rgba(0,0,0,0)', 0.249, 'rgba(0,0,0,0)', 0.25, 'rgba(26,110,20,0.55)', 0.449, 'rgba(26,110,20,0.55)', 0.45, 'rgba(60,190,35,0.75)', 0.649, 'rgba(60,190,35,0.75)', 0.65, 'rgba(124,255,58,0.9)', 0.849, 'rgba(124,255,58,0.9)', 0.85, 'rgba(230,255,150,1)', 1, 'rgba(255,255,215,1)'],
    deaths: [0, 'rgba(0,0,0,0)', 0.249, 'rgba(0,0,0,0)', 0.25, 'rgba(130,15,70,0.55)', 0.449, 'rgba(130,15,70,0.55)', 0.45, 'rgba(210,35,120,0.75)', 0.649, 'rgba(210,35,120,0.75)', 0.65, 'rgba(255,61,154,0.9)', 0.849, 'rgba(255,61,154,0.9)', 0.85, 'rgba(255,190,225,1)', 1, 'rgba(255,235,245,1)']
  }
};
const SCALES: readonly HeatmapWeightScale[] = ['log-clamp', 'log', 'clamp', 'pow', 'rank', 'linear-peak'];
function eventTypesOnMap(map: MapLibreMap): string[] {
  return (map.getStyle()?.layers ?? []).filter(layer => layer.id.startsWith(HEAT_LAYER_PREFIX)).map(layer => layer.id.slice(HEAT_LAYER_PREFIX.length));
}
function radiusExpression({
  radiusAtZ3,
  worldLockRadius
}: LabState): unknown {
  if (!worldLockRadius) return radiusAtZ3;
  // Double per zoom level → constant world-space footprint.
  return ['interpolate', ['exponential', 2], ['zoom'], 0, radiusAtZ3 / 8, 3, radiusAtZ3, 5, radiusAtZ3 * 4];
}

/** Sparkline of the decile histogram — one tall bar means the weights are flat. */
function sparkline(histogram: readonly number[]): string {
  const blocks = ' ▁▂▃▄▅▆▇█';
  return histogram.map(pct => blocks[Math.min(8, Math.round(pct / 12))] ?? '█').join('');
}
export function HeatmapLab() {
  const {
    map
  } = useMapContext();
  const [enabled, setEnabled] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [state, setState] = useState<LabState>(INITIAL);
  const [stats, setStats] = useState<Record<string, HeatmapCategoryStats>>({});
  const [copied, setCopied] = useState(false);

  // Deferred to an effect so SSR markup matches the first client render.
  useEffect(() => {
    setEnabled(new URLSearchParams(window.location.search).get('heatlab') === '1');
  }, []);
  const patch = (next: Partial<LabState>) => setState(prev => ({
    ...prev,
    ...next
  }));
  const weightConfig = useMemo<HeatmapWeightConfig>(() => ({
    scale: state.scale,
    clampPercentile: state.clampPercentile,
    powExponent: state.powExponent,
    hideBottomPercentile: state.hideBottomPercentile
  }), [state.scale, state.clampPercentile, state.powExponent, state.hideBottomPercentile]);

  // Re-subscribes on every state change, so `sync` always closes over fresh values —
  // and re-running it here is what makes a slider drag repaint immediately rather than
  // waiting for the next `idle`.
  useEffect(() => {
    if (!enabled || !map) return;
    const current = state;
    const sync = () => {
      if (map._removed) return;
      const source = map.getSource(SOURCE_ID) as {
        _data?: {
          geojson?: GeoJSON.FeatureCollection;
        };
      } | undefined;
      const geojson = source?._data?.geojson;
      if (geojson) {
        const nextStats = applyHeatmapWeights({
          geojson,
          config: weightConfig
        });
        setStats(nextStats);
        try {
          (map.getSource(SOURCE_ID) as unknown as {
            setData(d: unknown): void;
          }).setData(geojson);
        } catch {
          // Source mid-update.
        }
      }
      for (const eventType of eventTypesOnMap(map)) {
        const visible = !current.hidden.includes(eventType);
        const heatId = heatLayerId(eventType);
        try {
          map.setPaintProperty(heatId, 'heatmap-weight', ['get', 'w']);
          map.setPaintProperty(heatId, 'heatmap-radius', radiusExpression(current));
          map.setPaintProperty(heatId, 'heatmap-intensity', current.intensity);
          map.setPaintProperty(heatId, 'heatmap-opacity', current.opacity);
          if (current.ramp === 'production') {
            map.setPaintProperty(heatId, 'heatmap-color', heatmapColorExpression(eventType));
          } else if (isHeatmapCategoryKey(eventType)) {
            const stops = RAMPS[current.ramp][eventType];
            if (stops) {
              map.setPaintProperty(heatId, 'heatmap-color', ['interpolate', ['linear'], ['heatmap-density'], ...stops]);
            }
          }
          map.setLayoutProperty(heatId, 'visibility', visible ? 'visible' : 'none');
          for (const part of ['area', 'outline'] as const) {
            const id = part === 'area' ? areaLayerId(eventType) : outlineLayerId(eventType);
            if (!map.getLayer(id)) continue;
            const on = current.disks === 'on' || current.disks === 'outline' && part === 'outline';
            map.setLayoutProperty(id, 'visibility', on && visible ? 'visible' : 'none');
          }
          const markerId = markerLayerId(eventType);
          if (map.getLayer(markerId)) {
            map.setLayoutProperty(markerId, 'visibility', current.markers && visible ? 'visible' : 'none');
          }
        } catch {
          // Layer mid-update; the idle handler re-runs this.
        }
      }
    };
    sync();

    // `useHeatmapLayer` also re-asserts layer visibility from Redux on `idle`. Deferring
    // a frame guarantees the Lab's overrides (disks off, category solo) apply *after*
    // it, instead of depending on effect registration order.
    let frame = 0;
    const syncAfterMapSettles = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };
    map.on('idle', syncAfterMapSettles);
    return () => {
      cancelAnimationFrame(frame);
      map.off('idle', syncAfterMapSettles);
    };
  }, [enabled, map, weightConfig, state]);
  if (!enabled) return null;
  const label: React.CSSProperties = {
    display: 'block',
    margin: '7px 0'
  };
  const rowBetween: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8
  };
  const input: React.CSSProperties = {
    width: '100%',
    accentColor: '#8ef'
  };
  const field: React.CSSProperties = {
    flex: '0 0 150px',
    background: '#1c1c20',
    color: '#eee',
    border: '1px solid #3a3a42',
    borderRadius: 5,
    padding: 3
  };
  const btn: React.CSSProperties = {
    flex: 1,
    minWidth: 72,
    background: '#22323a',
    color: '#8ef',
    border: '1px solid #366',
    borderRadius: 5,
    padding: 5,
    cursor: 'pointer',
    fontSize: 11
  };
  const slider = (text: string, value: number, min: number, max: number, step: number, onChange: (n: number) => void, display?: string) => <label style={label}>
            <span style={rowBetween}>
                <span>{text}</span>
                <b style={{
        color: '#8ef'
      }}>{display ?? value}</b>
            </span>
            <input type="range" min={min} max={max} step={step} value={value} style={input} onChange={e => onChange(Number(e.target.value))} />
        </label>;
  const heading = (text_0: string) => <div style={{
    margin: '13px 0 3px',
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: '.09em',
    textTransform: 'uppercase',
    color: '#8ef'
  }}>
            {text_0}
        </div>;
  return <div style={{
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 20,
    width: 330,
    maxHeight: '92vh',
    overflow: 'auto',
    background: 'rgba(12,12,14,.96)',
    color: '#eee',
    font: '12px/1.45 ui-sans-serif, system-ui, sans-serif',
    border: '1px solid #333',
    borderRadius: 10,
    boxShadow: '0 12px 40px rgba(0,0,0,.6)'
  }}>
            <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '9px 11px',
      background: '#17171b',
      borderBottom: '1px solid #333'
    }}>
                <b>🔥 Heatmap Lab</b>
                <button type="button" style={{
        marginLeft: 'auto',
        background: '#2a2a32',
        color: '#eee',
        border: 0,
        borderRadius: 4,
        padding: '2px 8px',
        cursor: 'pointer'
      }} onClick={() => setCollapsed(c => !c)}>
                    {collapsed ? '+' : '–'}
                </button>
            </div>

            {!collapsed && <div style={{
      padding: '10px 12px 14px'
    }}>
                    <div style={{
        display: 'flex',
        gap: 5,
        flexWrap: 'wrap'
      }}>
                        {Object.entries(PRESETS).map(([name, preset]) => <button type="button" key={name} style={name === 'Old (broken)' ? {
          ...btn,
          background: '#3a2a2a',
          color: '#fbb',
          borderColor: '#633'
        } : btn} onClick={() => patch(preset)}>
                                {name}
                            </button>)}
                    </div>

                    {heading('Weight scaling — the big lever')}
                    <label style={{
        ...rowBetween,
        alignItems: 'center',
        margin: '5px 0'
      }}>
                        <span>fn</span>
                        <select style={field} value={state.scale} onChange={e_0 => patch({
          scale: e_0.target.value as HeatmapWeightScale
        })}>
                            {SCALES.map(s => <option key={s} value={s}>
                                    {s}
                                </option>)}
                        </select>
                    </label>
                    {slider('saturate at percentile', state.clampPercentile, 0.5, 1, 0.005, n => patch({
        clampPercentile: n
      }), `p${(state.clampPercentile * 100).toFixed(1)}`)}
                    {state.scale === 'pow' && slider('pow exponent', state.powExponent, 0.1, 1, 0.05, n_0 => patch({
        powExponent: n_0
      }))}

                    {heading('Drop the long tail')}
                    {slider('hide sparsest bins', state.hideBottomPercentile, 0, 0.95, 0.01, n_1 => patch({
        hideBottomPercentile: n_1
      }), `${Math.round(state.hideBottomPercentile * 100)}%`)}

                    {heading('Kernel')}
                    {slider('radius px @ zoom 3', state.radiusAtZ3, 2, 60, 1, n_2 => patch({
        radiusAtZ3: n_2
      }))}
                    <label style={{
        display: 'flex',
        gap: 7,
        alignItems: 'center',
        margin: '5px 0'
      }}>
                        <input type="checkbox" checked={state.worldLockRadius} style={{
          accentColor: '#8ef'
        }} onChange={e_1 => patch({
          worldLockRadius: e_1.target.checked
        })} />
                        lock radius to world size (2^zoom)
                    </label>
                    {slider('intensity', state.intensity, 0.1, 4, 0.05, n_3 => patch({
        intensity: n_3
      }))}
                    {slider('opacity', state.opacity, 0, 1, 0.05, n_4 => patch({
        opacity: n_4
      }))}

                    {heading('Colour')}
                    <label style={{
        ...rowBetween,
        alignItems: 'center',
        margin: '5px 0'
      }}>
                        <span>ramp</span>
                        <select style={field} value={state.ramp} onChange={e_2 => patch({
          ramp: e_2.target.value as RampKey
        })}>
                            <option value="production">production (cold→hot)</option>
                            <option value="hue-locked">hue-locked (green/pink)</option>
                            <option value="stepped">stepped (contour tiers)</option>
                        </select>
                    </label>

                    {heading('Overlays')}
                    <label style={{
        ...rowBetween,
        alignItems: 'center',
        margin: '5px 0'
      }}>
                        <span>cluster disks</span>
                        <select style={field} value={state.disks} onChange={e_3 => patch({
          disks: e_3.target.value as DiskMode
        })}>
                            <option value="off">off</option>
                            <option value="outline">outline only</option>
                            <option value="on">fill + outline</option>
                        </select>
                    </label>
                    <label style={{
        display: 'flex',
        gap: 7,
        alignItems: 'center',
        margin: '5px 0'
      }}>
                        <input type="checkbox" checked={state.markers} style={{
          accentColor: '#8ef'
        }} onChange={e_4 => patch({
          markers: e_4.target.checked
        })} />
                        hotspot pins
                    </label>
                    {Object.keys(stats).map(eventType_0 => <label key={eventType_0} style={{
        display: 'flex',
        gap: 7,
        alignItems: 'center',
        margin: '5px 0'
      }}>
                            <input type="checkbox" checked={!state.hidden.includes(eventType_0)} style={{
          accentColor: '#8ef'
        }} onChange={e_5 => patch({
          hidden: e_5.target.checked ? state.hidden.filter(t => t !== eventType_0) : [...state.hidden, eventType_0]
        })} />
                            show {eventType_0}
                        </label>)}

                    <div style={{
        marginTop: 10,
        padding: 8,
        background: '#111',
        border: '1px solid #2a2a2a',
        borderRadius: 6,
        font: '10px/1.6 ui-monospace, monospace',
        color: '#9c9'
      }}>
                        <div style={{
          color: '#777'
        }}>weight spread over 10 deciles — want it EVEN:</div>
                        {Object.entries(stats).map(([eventType_1, s_0]) => {
          const hist = weightDecileHistogram({
            stats: s_0,
            config: weightConfig
          });
          const drawn = s_0.sortedCounts.filter(c_0 => c_0 >= s_0.floorCount).length;
          return <div key={eventType_1}>
                                    {eventType_1.padEnd(7)}
                                    <span style={{
              color: '#8ef'
            }}>{sparkline(hist)}</span>
                                    {'  '}
                                    <span style={{
              color: '#777'
            }}>
                                        top decile {hist[0]}% · {drawn}/{s_0.binCount} bins · ≥{s_0.floorCount} events
                                    </span>
                                </div>;
        })}
                    </div>

                    <button type="button" style={{
        width: '100%',
        marginTop: 8,
        background: '#2a2a32',
        color: '#eee',
        border: '1px solid #444',
        borderRadius: 5,
        padding: 6,
        cursor: 'pointer'
      }} onClick={() => {
        void navigator.clipboard.writeText(JSON.stringify(state, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}>
                        {copied ? 'Copied ✓' : 'Copy config JSON'}
                    </button>
                </div>}
        </div>;
}